<script setup lang="ts">
import { useSiteWeeklyLimits } from "@/api/weekly-limit-query";
import {
	DataDefinition,
	DataType,
	DustDataType,
	VibrationSubType,
	WeatherDataType,
} from "@soft-db/data-formatter";
import { WeeklyLimitSummary } from "@soft-db/monitoring-api-client";
import { fromBase64 } from "@soft-db/soft-utils";
import { FormField, Select, SelectOption } from "@soft-db/ui";
import type { LiteralToPrimitiveDeep, UnionToIntersection } from "type-fest";

type IntersectedDefinition = UnionToIntersection<LiteralToPrimitiveDeep<DataDefinition>>;

const weeklyLimitModel = defineModel<string | null>("weeklyLimit");
const props = defineProps<{
	site: string;
	type?: DataType;
	subType?: VibrationSubType | WeatherDataType | DustDataType | "";
	required?: boolean;
	label?: string;
}>();

const { t } = useI18n();

const { data: weeklyLimits } = useSiteWeeklyLimits(computed(() => props.site));

const filteredLimits = computed<WeeklyLimitSummary[]>(() => {
	if (!weeklyLimits.value) return [];
	if (!props.type) return weeklyLimits.value;

	return weeklyLimits.value.filter((limit) => {
		const definition: IntersectedDefinition = JSON.parse(fromBase64(limit.query.query));
		if (definition.type === props.type && definition.subType === props.subType) return limit;
	});
});

const items = computed(() => [
	...(props.required ? [] : [{ name: t("common.none"), id: null }]),
	...filteredLimits.value,
]);
</script>

<template>
	<FormField class="w-full" :label="label ?? t('common.weeklyLimit')">
		<Select v-model="weeklyLimitModel" class="w-full">
			<SelectOption v-for="item of items" :key="item.id || ''" :value="item.id">
				{{ item.name }}
			</SelectOption>
		</Select>
	</FormField>
</template>
